import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useExcJpmamV1JpmamTaskQueryServiceJpmamTaskGet } from '@interxfi/api-client/generated/hooks/exc-jpmam-v1-jpmam-task-query-service-jpmam-task-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcJpmamV1JpmamTaskState } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { JpmamOrderFillsTable } from '../../../../../../../../../../features/vendor/jpmam/order/jpmam-order-fills-table';
import { JpmamTaskEntityMenu } from '../../../../../../../../../../features/vendor/jpmam/task/jpmam-task-entity-menu';
import { JpmamTaskEventSummary } from '../../../../../../../../../../features/vendor/jpmam/task/jpmam-task-event';
import { getJpmamTaskStateListItems } from '../../../../../../../../../../features/vendor/jpmam/task/jpmam-task-list-items';
import { getJpmamTaskDetailPath } from '../../../../../../../../../../features/vendor/jpmam/task/jpmam-task-routing';
import { JpmamTaskStatusPill } from '../../../../../../../../../../features/vendor/jpmam/task/jpmam-task-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/jpm/task/_task/$taskId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `jpmam-task-${params.taskId}`,
        label: 'Task',
        to: getJpmamTaskDetailPath(params.taskId),
        suffix: <CopyButton content={params.taskId} label="Copy Task ID" tooltip={{ copy: params.taskId, copied: 'Copied Task ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('JPMAM Task'),
  component: JpmamTaskDetail,
});

function getListItems(data: ExcJpmamV1JpmamTaskState | undefined) {
  const items = getJpmamTaskStateListItems(data);

  return {
    left: [
      items.data.operationType,
      ...match(items.data.spec)
        .returnType<ReadonlyListItem[]>()
        .with({ orderStatusNotify: P.not(P.nullish) }, ({ orderStatusNotify }) => [
          ...(orderStatusNotify.value.groupingId ? [orderStatusNotify.value.groupingId] : []),
          ...(orderStatusNotify.value.targetNetOrderStatusPill ? [orderStatusNotify.value.targetNetOrderStatusPill] : []),
          ...(orderStatusNotify.value.statusData
            ? [
                orderStatusNotify.value.statusData.nav,
                orderStatusNotify.value.statusData.settlementDate,
                orderStatusNotify.value.statusData.settlementTime,
                ...(orderStatusNotify.value.statusData.reason ? [orderStatusNotify.value.statusData.reason] : []),
              ]
            : []),
          orderStatusNotify.value.orderIds,
        ])
        .with({ orderStatePropagate: P.not(P.nullish) }, ({ orderStatePropagate }) => [
          orderStatePropagate.value.netOrderId,
          orderStatePropagate.value.targetOrderStatusPill,
          ...(orderStatePropagate.value.statusData
            ? [
                orderStatePropagate.value.statusData.nav,
                orderStatePropagate.value.statusData.settlementDate,
                orderStatePropagate.value.statusData.settlementTime,
                ...(orderStatePropagate.value.statusData.reason ? [orderStatePropagate.value.statusData.reason] : []),
              ]
            : []),
          orderStatePropagate.value.orderIds,
        ])
        .with({ orderReassign: P.not(P.nullish) }, ({ orderReassign }) => [orderReassign.value.targetNetOrderId, orderReassign.value.orderIds])
        .otherwise(() => STABLE_EMPTY_ARRAY_REF),
    ] as const satisfies ReadonlyListItem[],
    right: [items.jpmamTaskId, items.referenceId, items.metadata.createdAt, items.metadata.updatedAt] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load JPMAM task', 'view this JPMAM task');

function JpmamTaskDetail() {
  const { taskId } = Route.useParams();
  const request = { jpmamTaskId: taskId } as const;
  const { data, error, isPending } = useExcJpmamV1JpmamTaskQueryServiceJpmamTaskGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.jpmamTask), [data?.jpmamTask]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <JpmamTaskStatusPill isPending={showPending} size="large" status={data?.jpmamTask?.status} />
          </HorizontalStack>

          <JpmamTaskEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <JpmamTaskEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {data?.jpmamTask?.data?.spec && 'orderStatusNotify' in data.jpmamTask.data.spec ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Fills</Typography.Level3>
            <JpmamOrderFillsTable fills={data.jpmamTask.data.spec.orderStatusNotify.fills ?? STABLE_EMPTY_ARRAY_REF} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
